import styles from "./RVHighlightedString.module.scss";

type RVHighlightedStringProps = {
    val: string;
    highlightVal?: string;
};

/**
 * Highlights all occurrences of `highlightVal` within `val` using a styled span.
 *
 * @param val - The full string to display
 * @param highlightVal - The substring to highlight (case-insensitive)
 * @returns JSX.Element - The rendered string with highlights
 */
export function RVHighlightedString({ val, highlightVal }: RVHighlightedStringProps): JSX.Element {
    /**
     * Finds all start/end indices of highlightVal within val (case-insensitive).
     */
    function getHighlightMatchIndices(): Array<[number, number]> {
        const optionText = val.toLowerCase();
        const optionHighlight = highlightVal?.toLowerCase() || "";

        const spliceIndices: Array<[number, number]> = [];
        let idx = optionHighlight ? optionText.indexOf(optionHighlight) : -1;

        while (idx !== -1 && optionHighlight.length > 0) {
            spliceIndices.push([idx, idx + optionHighlight.length]);
            idx = optionText.indexOf(optionHighlight, idx + 1);
        }

        return spliceIndices;
    }

    /**
     * Returns an array of strings and highlighted spans for rendering.
     */
    function highlightValueMatches(): Array<JSX.Element | string> {
        const highlightMatches = getHighlightMatchIndices();
        const strBuffer: Array<JSX.Element | string> = [];
        let strPointer = 0;

        highlightMatches.forEach(([startIndex, endIndex]) => {
            if (startIndex > strPointer) {
                strBuffer.push(val.slice(strPointer, startIndex));
            }
            strBuffer.push(
                <span className={styles.highlight} key={`${startIndex}-${endIndex}`}>
                    {val.slice(startIndex, endIndex)}
                </span>
            );
            strPointer = endIndex;
        });

        if (strPointer < val.length) {
            strBuffer.push(val.slice(strPointer));
        }

        return strBuffer;
    }

    return (
        <span key={val} className={styles.highlightContainer}>
            {!!highlightVal ? highlightValueMatches() : val}
        </span>
    );
}
